import React, { useRef, useState, useEffect, useCallback } from "react";
import ReactPlayer from "react-player";
import { useRecoilState } from "recoil";
import { Link, useNavigate } from "react-router-dom";

import {
  stepFramesAtom,
  videoInfoAtom,
  frameRateAtom,
  currentFrameAtom,
  numOfFramesToSkipAtom,
  videoStateAtom,
  videoFilenameAtom,
  danceStepsAtom,
} from "../Recoil/atoms";
import StepsComponent from "./Prototype/StepsComponent";
// import AnimatedReactions from "./Prototype/AnimatedReactions";
import Bookmarks from "./Prototype/Bookmarks";
import { BsBookmark } from "react-icons/bs";
import { CiPlay1, CiPause1 } from "react-icons/ci";
import { FiCamera } from "react-icons/fi";
//  import "./CustomControls.css";
import { useLocation } from "react-router-dom";

const CustomVideoPlayer = () => {
  // Recoil states :
  const [stepFrames, setStepFrames] = useRecoilState(stepFramesAtom);
  const [videoInfo, setVideoInfo] = useRecoilState(videoInfoAtom);
  const [frameRate, setFrameRate] = useRecoilState(frameRateAtom);
  const [currentFrame, setCurrentFrame] = useRecoilState(currentFrameAtom);
  const [numOfFramesToSkip, setNumOfFramesToSkip] = useRecoilState(
    numOfFramesToSkipAtom
  );
  const [videoState, setVideoState] = useRecoilState(videoStateAtom);
  const [videoFilename, setVideoFilename] = useRecoilState(videoFilenameAtom);
  const [danceSteps, setDanceSteps] = useRecoilState(danceStepsAtom);

  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [currentStep, setCurrentStep] = useState(null);
  const [loop, setLoop] = useState(true);

  // console.log("videoInfo isssss:", videoInfo);
  // console.log("DanceSteps areeeeeeee :", danceSteps);
  const [showSteps, setShowSteps] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [mirrored, setMirrored] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [uploadedByUsername, setUploadedByUsername] = useState(null);
  const [uploadedByUserId, setUploadedByUserId] = useState(null);

  // Top of component after recoil + useState declarations:
  const [stepSelections, setStepSelections] = useState([]);
  const [mergedSteps, setMergedSteps] = useState([]); // Optional - if you want to keep track
  const COMBINE_AFTER_N = 4; // Or make this user configurable

  //New features for increasing interactivity
  const [bookmarks, setBookmarks] = useState([]);

  const [videos, setVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const [visibleStepLabel, setVisibleStepLabel] = useState(null);
  const [isLearnMode, setIsLearnMode] = useState(false);
  const [countdown, setCountdown] = useState(null);

  // Use useRef to persist the sessionId across re-renders
  const sessionIdRef = useRef(crypto.randomUUID());
  const [isListening, setIsListening] = useState(false);
  const socketRef = useRef(null);
  const learnModeTimeoutRef = useRef(null);
  const learnModeIntervalRef = useRef(null);

  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const maxReconnectAttempts = 5;
  const reconnectAttemptsRef = useRef(0);

  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);

  const speedOptions = [
    { label: "0.25x", value: 0.25 },
    { label: "0.5x", value: 0.5 },
    { label: "1x", value: 1 },
    { label: "1.25x", value: 1.25 },
  ];

  const [onStepComplete, setOnStepComplete] = useState(null);
  const [stepStartTime, setStepStartTime] = useState(null);
  const [isInStepSequence, setIsInStepSequence] = useState(false);

  // Camera and practice mode states
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraVideoRef, setCameraVideoRef] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [frameSendingInterval, setFrameSendingInterval] = useState(null);
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [similarityScore, setSimilarityScore] = useState(null);
  const [practiceCountdown, setPracticeCountdown] = useState(null);
  const [originalLoopState, setOriginalLoopState] = useState(true);

  // WebSocket for real-time frame processing
  const [practiceWebSocket, setPracticeWebSocket] = useState(null);
  const [isPracticeWebSocketConnected, setIsPracticeWebSocketConnected] =
    useState(false);

  // Debug log for onStepComplete changes
  useEffect(() => {
    console.log("onStepComplete changed:", onStepComplete);
  }, [onStepComplete]);

  // Auto-load video data when component mounts
  const location = useLocation();
  useEffect(() => {
    console.log("🎬 CustomVideoPlayer mounted - auto-loading video data");
    if (!location.state?.videoUrl) {
      fetchRandomVideo();
    }
  }, []); // Only fetch random video if not coming from a profile

  // Debug log for dance steps changes
  useEffect(() => {
    console.log("🎬 Dance steps updated:", {
      count: danceSteps?.length || 0,
      steps: danceSteps,
    });
  }, [danceSteps]);

  // Camera functionality
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 },
        },
      });
      setCameraStream(stream);
      setIsCameraActive(true);
      console.log("📹 Camera started successfully");
    } catch (error) {
      console.error("📹 Error starting camera:", error);
      alert("Could not access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
      setIsCameraActive(false);
      setIsPracticeMode(false);
      setPracticeCountdown(null);
      setLoop(originalLoopState);

      // Close WebSocket connection when camera stops
      if (practiceWebSocket) {
        practiceWebSocket.close();
        setPracticeWebSocket(null);
        setIsPracticeWebSocketConnected(false);
      }

      console.log("📹 Camera stopped");
    }
  };

  // Frame extraction and sending
  const extractAndSendFrame = async () => {
    if (!cameraVideoRef || !isPracticeMode) return;

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Set canvas size to match video dimensions
      canvas.width = cameraVideoRef.videoWidth;
      canvas.height = cameraVideoRef.videoHeight;

      // Draw current video frame to canvas
      ctx.drawImage(cameraVideoRef, 0, 0);

      // Convert to WebP format with compression
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/webp", 0.8); // 80% quality
      });

      // Convert blob to base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64Frame = reader.result.split(",")[1]; // Remove data:image/webp;base64, prefix

        // Send frame via WebSocket for real-time processing
        if (
          practiceWebSocket &&
          practiceWebSocket.readyState === WebSocket.OPEN
        ) {
          practiceWebSocket.send(
            JSON.stringify({
              type: "frame",
              frame: base64Frame,
              videoFilename: videoFilename,
              currentStep: currentStep?.newStepId || null,
              timestamp: Date.now(),
              sessionId: sessionIdRef.current,
            })
          );
        }
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error("📹 Error extracting frame:", error);
    }
  };

  // Start/stop frame sending based on practice mode
  useEffect(() => {
    if (
      isPracticeMode &&
      isCameraActive &&
      practiceCountdown === null &&
      isPracticeWebSocketConnected
    ) {
      // Send frames at 15fps (every 67ms) - only after countdown ends
      const interval = setInterval(extractAndSendFrame, 67);
      setFrameSendingInterval(interval);
      console.log(
        "📹 Started frame sending for practice mode (after countdown)"
      );
    } else {
      if (frameSendingInterval) {
        clearInterval(frameSendingInterval);
        setFrameSendingInterval(null);
        console.log("📹 Stopped frame sending");
      }
    }

    return () => {
      if (frameSendingInterval) {
        clearInterval(frameSendingInterval);
      }
    };
  }, [
    isPracticeMode,
    isCameraActive,
    practiceCountdown,
    isPracticeWebSocketConnected,
  ]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Setup WebSocket connection for practice mode
  const setupPracticeWebSocket = useCallback(() => {
    if (practiceWebSocket?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket("ws://localhost:51040/ws/practice");
      setPracticeWebSocket(ws);

      ws.onopen = () => {
        console.log("📹 Practice WebSocket connected");
        setIsPracticeWebSocketConnected(true);
      };

      ws.onclose = () => {
        console.log("📹 Practice WebSocket disconnected");
        setIsPracticeWebSocketConnected(false);
      };

      ws.onerror = (error) => {
        console.error("📹 Practice WebSocket error:", error);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.similarityScore !== undefined) {
            setSimilarityScore(data.similarityScore);
          }
        } catch (error) {
          console.error("📹 Error processing WebSocket message:", error);
        }
      };
    } catch (error) {
      console.error("📹 Error setting up Practice WebSocket:", error);
    }
  }, []);

  // Initialize Practice WebSocket connection
  useEffect(() => {
    // Only setup WebSocket when practice mode is about to start
    // Don't connect automatically when camera starts
  }, []);

  const handleSeek = (time) => {
    if (playerRef.current) {
      playerRef.current.seekTo(time);
    }
  };

  const handleDeleteBookmark = (index) => {
    setBookmarks((prev) => prev.filter((_, i) => i !== index));
  };

  const addBookmark = () => {
    const currentTime = playerRef.current.getCurrentTime();
    const newBookmark = {
      time: currentTime,
      description: `Bookmark at ${Math.floor(currentTime / 60)}:${Math.floor(
        currentTime % 60
      )
        .toString()
        .padStart(2, "0")}`,
    };
    setBookmarks((prev) => [...prev, newBookmark]);
  };

  const toggleSteps = () => {
    setShowSteps(!showSteps); // Toggle visibility of steps
  };

  const togglePlay = () => {
    setPlaying(!playing);
  };

  const toggleMirroring = () => {
    setMirrored(!mirrored); // Toggle mirroring state
  };

  // CAMERA
  const [splitScreen, setSplitScreen] = useState(false);

  const toggleCamera = async () => {
    if (!isCameraActive) {
      await startCamera();
      setSplitScreen(true);
    } else {
      stopCamera();
      setSplitScreen(false);
      setIsPracticeMode(false);
    }
  };

  const togglePracticeMode = () => {
    if (!isCameraActive) {
      alert("Please start camera first to enter practice mode");
      return;
    }

    if (!isPracticeMode) {
      // Starting practice mode
      setOriginalLoopState(loop); // Save current loop state
      setLoop(false); // Turn off loop for practice
      setIsPracticeMode(true);

      // Setup WebSocket connection for practice mode
      setupPracticeWebSocket();

      // Start countdown
      let count = 5;
      setPracticeCountdown(count);

      const countdownInterval = setInterval(() => {
        count -= 1;
        if (count <= 0) {
          clearInterval(countdownInterval);
          setPracticeCountdown(null);

          // Start video from beginning
          if (playerRef.current) {
            playerRef.current.seekTo(0, "seconds");
            setPlaying(true);
          }
        } else {
          setPracticeCountdown(count);
        }
      }, 1000);

      console.log("🎯 Practice mode enabled with countdown");
    } else {
      // Stopping practice mode
      setIsPracticeMode(false);
      setPracticeCountdown(null);
      setLoop(originalLoopState); // Restore original loop state
      setPlaying(false);

      // Close WebSocket connection
      if (practiceWebSocket) {
        practiceWebSocket.close();
        setPracticeWebSocket(null);
        setIsPracticeWebSocketConnected(false);
      }

      console.log("🎯 Practice mode disabled");
    }
  };

  const [showLearnDisclaimer, setShowLearnDisclaimer] = useState(false);

  const fetchRandomVideo = async () => {
    console.log("Handling next video heyy");
    // Reset all learn mode state
    stopLearnMode();

    // Reset practice mode state when switching videos
    setIsPracticeMode(false);
    setPracticeCountdown(null);
    setLoop(originalLoopState);
    setSimilarityScore(null);
    if (frameSendingInterval) {
      clearInterval(frameSendingInterval);
      setFrameSendingInterval(null);
    }

    // Close WebSocket connection when switching videos
    if (practiceWebSocket) {
      practiceWebSocket.close();
      setPracticeWebSocket(null);
      setIsPracticeWebSocketConnected(false);
    }

    setLoading(true);
    try {
      const response = await fetch(
        "https://dance-ai-app.azurewebsites.net/api/videosFromStorageLabelled",
        {
          headers: {
            "X-Session-ID": sessionIdRef.current,
          },
        }
      );
      const data = await response.json();
      if (data.url) {
        await fetchVideo(data.url);
        console.log("The data is :", data);
        setVideoFilename(data.videoFilename);
        setDanceSteps(data.steps);
        setUploadedByUsername(data.username);
        setUploadedByUserId(data.userId);

        try {
          const frameResponse = await fetch(
            "https://dance-ai-app.azurewebsites.net/get_frame_info",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Session-ID": sessionIdRef.current,
                Accept: "application/json",
              },
              body: JSON.stringify({ videoFilename: data.videoFilename }),
            }
          );

          if (!frameResponse.ok) {
            throw new Error(`HTTP error! status: ${frameResponse.status}`);
          }

          const frameData = await frameResponse.json();
          const frameRate = parseInt(frameData.frameRate);
          console.log("Frame rate received:", frameRate);
          setFrameRate(frameRate);
        } catch (frameError) {
          console.error("Error getting frame info:", frameError);
          setFrameRate(30);
        }
      } else {
        console.error("Error fetching video:", data.error);
      }
    } catch (error) {
      console.error("Error fetching video:", error);
    }
    setLoading(false);
    setPlaying(true); // Auto-play after loading new video
  };

  const fetchVideo = async (url) => {
    try {
      const response = await fetch(
        "https://dance-ai-app.azurewebsites.net/api/fetch_video",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Session-ID": sessionIdRef.current,
            Accept: "application/json",
          },
          body: JSON.stringify({ url }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.message === "Video fetched") {
        setVideoUrl(data.videoUrl);
        setVideoInfo({ videoUrl: data.videoUrl });
        setPlaying(true); // Auto-play after loading new video
        console.log("the video url is : ", data.videoUrl);
      } else {
        console.error("Error fetching video:", data.error);
      }
    } catch (error) {
      console.error("Error fetching video:", error);
    }
  };

  // Add this useEffect to handle video loading state
  useEffect(() => {
    if (videoUrl || videoInfo.videoUrl) {
      setLoading(true);
      setIsVideoReady(false);
      setPlaying(true);
      // Add a delay to ensure the video is loaded
      const timer = setTimeout(() => {
        setLoading(false);
        setIsVideoReady(true);
      }, 2000); // 2 second delay

      return () => clearTimeout(timer);
    }
  }, [videoUrl, videoInfo.videoUrl]);

  const playStep = (step) => {
    if (!step || !isVideoReady || !playerRef.current) return;

    if (step.isCombined) {
      console.log("🎥 playStep called for combined step:", {
        stepId: step.newStepId,
        frames: `${step.keyFrameIn} - ${step.keyFrameOut}`,
        isInStepSequence: isInStepSequence,
        totalSteps: step.originalSteps.length,
        step: step,
      });
    } else {
      console.log("🎥 playStep called:", {
        stepId: step.newStepId,
        frames: `${step.keyFrameIn} - ${step.keyFrameOut}`,
        isInStepSequence: isInStepSequence,
        step: step,
      });
    }

    setCurrentStep(step);
    const startSeconds = step.keyFrameIn / frameRate;
    const endSeconds = step.keyFrameOut / frameRate;

    console.log("🎥 Step timing:", {
      startSeconds: startSeconds.toFixed(2),
      endSeconds: endSeconds.toFixed(2),
      frameRate: frameRate,
      duration: `${((endSeconds - startSeconds) * 1000).toFixed(0)}ms`,
    });

    // Set the step start time to prevent immediate callback execution
    setStepStartTime(Date.now());

    // Ensure the video is at the correct position before playing
    playerRef.current.seekTo(startSeconds, "seconds");

    // Small delay to ensure seek is complete before playing
    setTimeout(() => {
      setPlaying(true);

      if (playerRef.current.loopInterval) {
        clearInterval(playerRef.current.loopInterval);
        playerRef.current.loopInterval = null;
      }

      if (playerRef.current.stopTimeout) {
        clearInterval(playerRef.current.stopTimeout);
        playerRef.current.stopTimeout = null;
      }

      if (loop) {
        playerRef.current.loopInterval = setInterval(() => {
          if (
            playerRef.current !== null &&
            playerRef.current.getCurrentTime() >= endSeconds
          ) {
            playerRef.current.seekTo(startSeconds, "seconds");
          }
        }, 100);
      } else {
        // Use setInterval to continuously check for step completion
        playerRef.current.stopTimeout = setInterval(() => {
          if (
            playerRef.current &&
            playerRef.current.getCurrentTime() >= endSeconds
          ) {
            console.log("Step completed:", step);
            setPlaying(false);
            clearInterval(playerRef.current.stopTimeout);
            playerRef.current.stopTimeout = null;

            // Only call callback if step has been playing for at least 3 seconds
            // and we have a valid callback
            if (
              onStepComplete &&
              stepStartTime &&
              Date.now() - stepStartTime > 3000
            ) {
              console.log(
                "Calling step completion callback after step actually finished"
              );
              onStepComplete();
            } else {
              console.log(
                "No step completion callback set or step just started (elapsed time:",
                stepStartTime ? Date.now() - stepStartTime : "no start time",
                "ms)"
              );
            }
          }
        }, 100); // Check less frequently to avoid race conditions
      }
    }, 500); // 500ms delay to ensure seek is complete
  };

  const handleStepChange = (step) => {
    if (!step) return;
    console.log("Step changed to:", step);
    setCurrentStep(step);
    playStep(step);
  };

  // Update the useEffect for currentStep
  useEffect(() => {
    console.log("currentStep useEffect triggered:", {
      currentStep,
      isVideoReady,
      isInStepSequence,
      onStepComplete,
    });

    if (currentStep && isVideoReady) {
      // Always call playStep when we have a currentStep and video is ready
      console.log("Calling playStep for current step");
      playStep(currentStep);
    }
  }, [currentStep, isVideoReady, loop]);

  // Set camera stream to video element when available
  useEffect(() => {
    if (cameraVideoRef && cameraStream) {
      cameraVideoRef.srcObject = cameraStream;
    }
  }, [cameraVideoRef, cameraStream]);

  const toggleLoop = () => {
    setLoop(!loop);
    if (!loop) {
      playStep(currentStep); // Restart playing with loop if toggled on
    }
  };

  const handleNextStep = () => {
    console.log("current step is ", currentStep, danceSteps);
    if (!currentStep || !danceSteps) return;

    const nextStepIndex = currentStep.newStepId + 1;
    if (nextStepIndex < danceSteps.length) {
      const nextStep = danceSteps[nextStepIndex];
      handleStepChange(nextStep);
    }
  };

  const handlePreviousStep = () => {
    if (!currentStep || !danceSteps) return;

    const prevStepIndex = currentStep.newStepId - 1;
    if (prevStepIndex >= 0) {
      const prevStep = danceSteps[prevStepIndex];
      handleStepChange(prevStep);
    }
  };

  // const location = useLocation();

  useEffect(() => {
    if (location.state?.videoUrl) {
      setIsLearnMode(false);
      setVisibleStepLabel(null);
      setCountdown(null);
      setCurrentStep(null);

      // Reset practice mode state
      setIsPracticeMode(false);
      setPracticeCountdown(null);
      setLoop(originalLoopState);
      setSimilarityScore(null);
      if (frameSendingInterval) {
        clearInterval(frameSendingInterval);
        setFrameSendingInterval(null);
      }

      // Close WebSocket connection when loading from profile
      if (practiceWebSocket) {
        practiceWebSocket.close();
        setPracticeWebSocket(null);
        setIsPracticeWebSocketConnected(false);
      }

      setVideoUrl(location.state.videoUrl);
      setVideoInfo({ videoUrl: location.state.videoUrl });
      setVideoFilename(location.state.videoFilename);
      setUploadedByUsername(location.state.username);
      setUploadedByUserId(location.state.userId);

      // Fetch steps for the specific video
      fetch("https://dance-ai-app.azurewebsites.net/api/get_steps_for_video", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ videoFilename: location.state.videoFilename }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then((data) => {
          console.log(
            "we have received steps data from the backend now we will use them only."
          );
          setDanceSteps(data.steps);
        })
        .catch((error) => {
          console.error("Error fetching steps:", error);
        });

      // Fetch Frame Info with proper error handling
      fetch("https://dance-ai-app.azurewebsites.net/get_frame_info", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ videoFilename: location.state.videoFilename }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then((data) => {
          const frameRate = parseInt(data.frameRate);
          console.log("Frame rate received:", frameRate);
          setFrameRate(frameRate);
        })
        .catch((error) => {
          console.error("Error getting frame info:", error);
          // Set a default frame rate if the request fails
          setFrameRate(30); // Default to 30fps
        });
    }
  }, [location.state]);

  const navigate = useNavigate();

  const handleProfileClick = () => {
    navigate(`/profile/${uploadedByUserId}`, {
      state: {
        profileData: {
          username: uploadedByUsername,
          bio: "Dancer & Creator", // Default bio
          profileImage: "", // Default profile image
          uid: uploadedByUserId,
        },
      },
    });
  };

  const toggleListening = async () => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.error("🎤 WebSocket not connected");
      return;
    }

    try {
      // Always send the opposite command of current state
      const message = {
        command: isListening ? "stop_listening" : "start_listening",
      };

      console.log(`🎤 Sending ${message.command} command...`);
      socketRef.current.send(JSON.stringify(message));

      // Update local state immediately for better UX
      setIsListening(!isListening);
    } catch (error) {
      console.error("🎤 Error toggling listening:", error);
      // Revert state if there was an error
      setIsListening(false);
    }
  };

  const handleSpeedChange = (speed) => {
    setPlaybackRate(speed);
    setShowSpeedOptions(false);
  };

  const setupWebSocket = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      // console.log("🎤 WebSocket already connected");
      return;
    }

    try {
      // console.log("🎤 Attempting to connect to WebSocket...");
      const ws = new WebSocket("ws://localhost:51042/ws");
      socketRef.current = ws;

      ws.onopen = () => {
        // console.log("🎤 WebSocket connected successfully");
        setIsWebSocketConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onclose = (event) => {
        // console.log("🎤 WebSocket disconnected:", event.code, event.reason);
        setIsWebSocketConnected(false);
        setIsListening(false);

        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1;
            // console.log(
            //   `🎤 Attempting to reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
            // );
            setupWebSocket();
          }, 3000);
        }
      };

      ws.onerror = (error) => {
        // console.error("🎤 WebSocket error:", error);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // console.log("🎤 Received message from server:", data);

          if (data.error) {
            // console.error("🎤 Server error:", data.error);
            if (data.error === "Speech service not initialized") {
              // console.log("🎤 Speech service not initialized on server");
              setIsListening(false);
            }
            return;
          }

          // Handle different actions from the server
          switch (data.action) {
            case "playNextStep":
              console.log("🎤 Command received: Play Next Step");
              handleNextStep();
              break;
            case "playPreviousStep":
              console.log("🎤 Command received: Play Previous Step");
              handlePreviousStep();
              break;
            case "compareStep":
              console.log("🎤 Command received: Compare Step");
              break;
            case "playRandomStep":
              console.log("🎤 Command received: Play Random Step");
              break;
            case "playVideo":
              console.log("🎤 Command received: Play Video");
              setPlaying(true);
              break;
            case "pauseVideo":
              console.log("🎤 Command received: Pause Video");
              setPlaying(false);
              break;
            case "setSpeed":
              console.log("🎤 Command received: Set Speed", data.speed);
              if (data.speed && !isNaN(data.speed)) {
                handleSpeedChange(parseFloat(data.speed));
              }
              break;
            case "speech_started":
              console.log("🎤 Speech recognition started on server");
              setIsListening(true);
              break;
            case "speech_stopped":
              console.log("🎤 Speech recognition stopped on server");
              setIsListening(false);
              break;
            default:
              console.log("🎤 Unknown action received:", data.action);
          }
        } catch (error) {
          console.error("🎤 Error processing message:", error);
        }
      };
    } catch (error) {
      console.error("🎤 Error setting up WebSocket:", error);
    }
  }, []);

  // Initialize WebSocket connection
  useEffect(() => {
    setupWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [setupWebSocket]);

  // Learn the steps
  const handleLearningModeClick = async () => {
    if (isLearnMode) return;
    setIsLearnMode(true);
    console.log("🎯 Entering Learn Mode...");

    if (
      !danceSteps ||
      danceSteps.length === 0 ||
      !playerRef.current ||
      !isVideoReady
    ) {
      console.warn("❌ Steps not loaded or video not ready.");
      return;
    }

    // Merge steps that are very close
    const mergedSteps = [];
    const FRAME_THRESHOLD = 15;

    for (let i = 0; i < danceSteps.length; i++) {
      const current = { ...danceSteps[i] };
      if (mergedSteps.length > 0) {
        const prev = mergedSteps[mergedSteps.length - 1];
        // console.log("current step is and prev step is ")
        if (current.keyFrameOut - prev.keyFrameOut <= FRAME_THRESHOLD) {
          // Merge with previous
          prev.keyFrameOut = current.keyFrameOut;
          continue;
        }
      }
      mergedSteps.push(current);
    }

    console.log("Merged steps are :", mergedSteps);

    // Function to wait for N milliseconds
    const wait = (ms) => new Promise((res) => setTimeout(res, ms));

    // Function to play a step
    const playStepAndWait = async (step, label) => {
      console.log(`▶️ Playing Step ${label}`, step);
      const start = step.keyFrameIn / frameRate;
      const end = step.keyFrameOut / frameRate;

      playerRef.current.seekTo(start, "seconds");
      setPlaying(true);

      setVisibleStepLabel(label);

      // Store timeout reference
      await new Promise((resolve) => {
        learnModeTimeoutRef.current = setTimeout(() => {
          setVisibleStepLabel(null);
          setPlaying(false);
          resolve();
        }, ((end - start) * 1000) / playbackRate);
      });
    };

    const SEQUENCE_SIZE = 4; // teach 4 steps at a time
    const REPEAT_COUNT = 3; // each step is repeated 3 times
    const BLOCK_PAUSE = 2000; // 1 sec pause between steps
    const START_DELAY = 5000; // 5 seconds before starting

    let count = 5;
    setCountdown(count);

    const countdownInterval = setInterval(() => {
      count -= 1;
      if (count <= 0) {
        clearInterval(countdownInterval);
        setCountdown(null);
      } else {
        setCountdown(count);
      }
    }, 1000);

    playerRef.current.seekTo(0, "seconds");
    setPlaying(false);

    // Optionally show a hint to user
    await wait(START_DELAY);
    console.log("⏳ Starting Learn Mode sequence");

    let cumulativeSteps = [];

    for (let i = 0; i < mergedSteps.length; i += SEQUENCE_SIZE) {
      const block = mergedSteps.slice(i, i + SEQUENCE_SIZE);
      cumulativeSteps = [...cumulativeSteps, ...block];

      console.log("Cummulative steps are ", cumulativeSteps);

      // Repeat each step in the block
      for (let repeat = 0; repeat < REPEAT_COUNT; repeat++) {
        for (let j = 0; j < block.length; j++) {
          await playStepAndWait(block[j], `#${i + j + 1}`);
          // await wait(BLOCK_PAUSE);
        }
      }

      // Repeat the entire block
      for (let repeat = 0; repeat < REPEAT_COUNT; repeat++) {
        for (let j = 0; j < block.length; j++) {
          await playStepAndWait(block[j], `#${i + j + 1}`);
          // await wait(BLOCK_PAUSE);
        }
      }

      // Cumulative repeat
      for (let repeat = 0; repeat < REPEAT_COUNT; repeat++) {
        for (let j = 0; j < cumulativeSteps.length; j++) {
          await playStepAndWait(cumulativeSteps[j], `#${j + 1}`);
          // await wait(BLOCK_PAUSE);
        }
      }
    }
    setIsLearnMode(false);
    console.log("✅ Learn Mode finished.");
  };

  const stopLearnMode = () => {
    setIsLearnMode(false);
    setVisibleStepLabel(null);
    setCountdown(null);
    setPlaying(false);
    setCurrentStep(null);

    // 🧹 Clear pending timeouts/intervals to avoid leftover triggers
    if (learnModeTimeoutRef.current) {
      clearTimeout(learnModeTimeoutRef.current);
      learnModeTimeoutRef.current = null;
    }

    if (learnModeIntervalRef.current) {
      clearInterval(learnModeIntervalRef.current);
      learnModeIntervalRef.current = null;
    }
  };

  return (
    <div className="flex flex-grow h-[inherit] relative bg-black w-full">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="text-white text-xl">Loading video...</div>
        </div>
      )}
      <div
        className={`h-full ${mirrored ? "scale-x-[-1]" : "scale-x-[1]"} ${
          splitScreen ? "w-1/2" : "w-full"
        } relative z-10`}
        style={{ transformOrigin: "center" }}
      >
        <ReactPlayer
          ref={playerRef}
          url={videoUrl || videoInfo.videoUrl}
          playing={playing}
          // controls={!isLearnMode} // hide controls in Learn Mode
          width="100%"
          height="100%"
          loop={loop}
          playbackRate={playbackRate}
          onReady={() => {
            setLoading(false);
            setIsVideoReady(true);
            setPlaying(true);
          }}
          onError={(e) => {
            console.error("Video player error:", e);
            setLoading(false);
          }}
          onEnded={() => {
            if (isPracticeMode) {
              console.log(
                "🎯 Video ended during practice mode, exiting practice mode"
              );
              setIsPracticeMode(false);
              setPracticeCountdown(null);
              setLoop(originalLoopState);
              setPlaying(false);
            }
          }}
          onProgress={({ playedSeconds }) => {
            if (currentStep && !loop) {
              const endSeconds = currentStep.keyFrameOut / frameRate;
              if (playedSeconds >= endSeconds) {
                setPlaying(false);
              }
            }
          }}
        />
      </div>

      {countdown !== null && (
        <div className="absolute top-16 left-1/2 transform -translate-x-1/2 bg-white text-black px-6 py-3 rounded-lg shadow-lg text-4xl font-bold z-50">
          Starting in {countdown}
        </div>
      )}

      {visibleStepLabel && (
        <div className="absolute top-20 left-5 bg-white text-black px-4 py-2 rounded-lg shadow-md z-50 text-xl font-bold">
          Step {visibleStepLabel}
        </div>
      )}

      {/* {isLearnMode && countdown === null && (
        <div className="absolute top-[100px] left-5 bg-yellow-400 text-black px-4 py-2 rounded text-lg font-medium z-50">
          Learn Mode: Follow the steps!
        </div>
      )} */}

      {/* Practice Countdown Display */}
      {practiceCountdown !== null && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/90 text-white px-8 py-6 rounded-lg shadow-lg text-6xl font-bold z-50">
          Get Ready: {practiceCountdown}
        </div>
      )}

      {/* ALL THE BUTTONS */}
      <button
        className={`absolute top-5 left-1/3 transform -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-gray-800/80 
            hover:bg-gray-700/80 rounded-lg transition-colors text-white z-50`}
        onClick={toggleCamera}
      >
        <FiCamera size={24} />
        {isCameraActive ? "Close Camera" : "Open Camera"}
      </button>

      <button
        className={`absolute top-5 right-1/3 transform translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-gray-800/80 
        hover:bg-gray-700/80 rounded-lg transition-colors text-white z-50`}
        onClick={addBookmark}
      >
        <BsBookmark className="w-4 h-4" />
        Bookmark
      </button>

      {isCameraActive && (
        <button
          className={`absolute top-16 left-5 flex items-center gap-2 px-4 py-2 ${
            isPracticeMode
              ? "bg-green-600 hover:bg-green-700"
              : isPracticeWebSocketConnected
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-500 cursor-not-allowed"
          } rounded-lg transition-colors text-white z-50`}
          onClick={togglePracticeMode}
          disabled={!isPracticeWebSocketConnected}
          title={
            !isPracticeWebSocketConnected
              ? "Connecting to practice service..."
              : ""
          }
        >
          {isPracticeMode ? "Stop Practice" : "Start Practice"}
          {!isPracticeWebSocketConnected && " 🔄"}
        </button>
      )}

      {/* WebSocket Connection Status */}
      {isCameraActive && !isPracticeWebSocketConnected && (
        <div className="absolute left-5 top-28 bg-yellow-600 text-white p-2 rounded-lg text-sm font-bold z-50">
          Connecting to practice service...
        </div>
      )}

      {/* Similarity Score Display */}
      {isPracticeMode && similarityScore !== null && (
        <div className="absolute left-5 top-28 bg-black/80 text-white p-3 rounded-lg text-lg font-bold z-50">
          Score: {Math.round(similarityScore * 100)}%
        </div>
      )}

      <div
        className={`absolute bottom-[35px] left-1/2 translate-x-[-50%] w-1/2 items-center justify-center transition-opacity duration-300 ease-in-out opacity-100 h-16 flex gap-5 z-50`}
      >
        {/* Play/Pause Button */}
        <button
          className="w-1/6 text-white rounded-lg p-2 bg-blue-600 hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          onClick={() => setPlaying((p) => !p)}
        >
          {playing ? <CiPause1 size={24} /> : <CiPlay1 size={24} />}
          {playing ? "Pause" : "Play"}
        </button>
        {/* Learn Button with Disclaimer */}
        <div className="relative">
          <button
            className="text-white rounded-lg py-2 px-6 bg-blue-600 hover:bg-blue-700 transition-colors flex items-center justify-center"
            onClick={() => {
              if (isLearnMode) {
                stopLearnMode();
              } else {
                handleLearningModeClick();
              }
            }}
            onMouseEnter={() => setShowLearnDisclaimer(true)}
            onMouseLeave={() => setShowLearnDisclaimer(false)}
            onFocus={() => setShowLearnDisclaimer(true)}
            onBlur={() => setShowLearnDisclaimer(false)}
          >
            {isLearnMode ? "Stop" : "Learn"}
          </button>
          {showLearnDisclaimer && !isLearnMode && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black text-white text-xs rounded px-3 py-2 shadow-lg z-50 w-64 text-center">
              <b>Learn Mode:</b> This will auto-play all steps with guidance.
              <br />
              <span className="text-blue-300">
                For manual review, use the Play button in the Steps pane.
              </span>
            </div>
          )}
        </div>
        {/* Speed Change Button */}
        <div className="relative">
          <button
            className="text-white rounded-lg py-2 px-6 bg-gray-800/80 hover:bg-gray-700/80 transition-colors flex items-center justify-center"
            onClick={() => setShowSpeedOptions(!showSpeedOptions)}
          >
            {playbackRate}x
          </button>
          {showSpeedOptions && (
            <div className="absolute bottom-full left-0 mb-2 bg-gray-800/90 rounded-lg shadow-lg overflow-hidden z-50">
              {speedOptions.map((option) => (
                <button
                  key={option.value}
                  className={`w-full px-4 py-2 text-white hover:bg-gray-700/80 transition-colors ${
                    playbackRate === option.value ? "bg-blue-600" : ""
                  }`}
                  onClick={() => handleSpeedChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          className="w-1/6 text-white rounded-lg p-2 bg-gray-800/80 hover:bg-gray-700/80 transition-colors"
          onClick={toggleLoop}
        >
          {loop ? "Stop Loop" : "Loop"}
        </button>
        <button
          className="w-1/6 text-white rounded-lg p-2 bg-gray-800/80 hover:bg-gray-700/80 transition-colors"
          onClick={toggleMirroring}
        >
          {mirrored ? "Unmirror" : "Mirror"}
        </button>
        <button
          className="w-1/4 text-white rounded-lg py-2 px-2 bg-gray-800/80 hover:bg-gray-700/80 transition-colors"
          onClick={fetchRandomVideo}
        >
          Next Video
        </button>
        {/* <button
          className={`w-1/6 text-white rounded-lg p-2 ${
            isListening
              ? "bg-red-700 hover:bg-red-800"
              : isWebSocketConnected
              ? "bg-gray-800/80 hover:bg-gray-700/80"
              : "bg-gray-500 cursor-not-allowed"
          } transition-colors`}
          onClick={toggleListening}
          disabled={!isWebSocketConnected}
          title={
            !isWebSocketConnected
              ? "Connecting to voice service..."
              : isListening
              ? "Stop Voice Control"
              : "Start Voice Control"
          }
        >
          {!isWebSocketConnected
            ? "🔄 Connecting..."
            : isListening
            ? "🎤 Stop"
            : "🎤 Start"}
        </button> */}
      </div>

      <Link
        to={`/profile/${uploadedByUserId}`}
        state={{
          profileData: {
            username: uploadedByUsername,
            bio: "Dancer & Creator",
            profileImage: "",
            uid: uploadedByUserId,
          },
        }}
        className="absolute top-5 left-5 text-white bg-black/50 px-4 py-2 rounded-lg text-sm z-50 hover:underline"
        style={{ marginTop: isCameraActive ? "120px" : "0" }}
      >
        {" "}
        <span className="font-semibold">@{uploadedByUsername}</span>
      </Link>

      {/* Reactions for UI/UX */}
      {/* <div className="absolute bottom-[100px] left-[20px] flex flex-col gap-2">
        <AnimatedReactions/>
      </div> */}

      {/* Display Bookmarks */}
      {bookmarks.length > 0 && (
        <Bookmarks
          bookmarks={bookmarks}
          duration={playerRef.current?.getDuration() || 0}
          onSeek={handleSeek}
          onDelete={handleDeleteBookmark}
        />
      )}
      <div
        className={`absolute top-2 right-2 flex flex-col items-center justify-start z-50 pt-0 pr-[10px]`}
      >
        {" "}
        {/*rights-controls */}
        <div className="relative">
          <StepsComponent
            setCurrentStep={setCurrentStep}
            setOnStepComplete={setOnStepComplete}
            setIsInStepSequence={setIsInStepSequence}
            frameRate={frameRate}
            playing={playing}
            setPlaying={setPlaying}
            onClearSelection={() => {
              setCurrentStep(null);
              setPlaying(true);
            }}
          />
        </div>
      </div>
      {/* Camera Section */}
      {splitScreen && (
        <div className="h-full w-1/2 bg-black flex items-center justify-center absolute right-0 top-0 z-20">
          <video
            ref={setCameraVideoRef}
            autoPlay
            playsInline
            muted
            className="h-full w-full object-cover"
            style={{ transform: mirrored ? "scaleX(-1)" : "scaleX(1)" }}
          />
          {isPracticeMode && (
            <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-lg text-sm font-bold z-30">
              PRACTICE MODE
            </div>
          )}
        </div>
      )}

      {/* Add status indicator */}
      {isListening && (
        <div className="absolute bottom-[100px] left-1/2 transform -translate-x-1/2 bg-black/80 text-white p-4 rounded-lg text-sm">
          Listening for voice commands...
        </div>
      )}
    </div>
  );
};

export default CustomVideoPlayer;
